import moment from 'moment-timezone';

import { RAW_PERSONAL_TIMELINE_WEEK } from './crewMemberTimelineRaw';

const shiftTimestampByDays = (timestamp, dayOffset) => {
	if (!timestamp) return timestamp;

	const original = moment.utc(timestamp);
	if (!original.isValid()) return timestamp;

	return original.clone().add(dayOffset, 'days').toISOString();
};

const remapMock = (mock, dayOffset) => ({
	...mock,
	shifts: (mock?.shifts || []).map((shift) => ({
		...shift,
		fromTimestamp: shiftTimestampByDays(shift.fromTimestamp, dayOffset),
		toTimestamp: shiftTimestampByDays(shift.toTimestamp, dayOffset),
		autoCloseTimestamp: shift.autoCloseTimestamp
			? shiftTimestampByDays(shift.autoCloseTimestamp, dayOffset)
			: null,
	})),
	activitiesV2: (mock?.activitiesV2 || []).map((item) => ({
		...item,
		fromTimestamp: shiftTimestampByDays(item.fromTimestamp, dayOffset),
		toTimestamp: shiftTimestampByDays(item.toTimestamp, dayOffset),
		activity: {
			...item.activity,
			...(item.activity?.extraActivities
				? {
						extraActivities: item.activity.extraActivities.map((extra) => ({
							...extra,
							fromTimestamp: shiftTimestampByDays(extra.fromTimestamp, dayOffset),
							toTimestamp: shiftTimestampByDays(extra.toTimestamp, dayOffset),
						})),
					}
				: {}),
		},
	})),
});

/**
 * The raw mock uses Sunday 2026-03-22 as D0. To preserve the weekday pattern,
 * we align that Sunday to the Sunday of the user's currently visible week:
 *   - US locale (Sun-first week): mock Sunday → user's current-week Sunday (column 0).
 *   - non-US (Mon-first week):    mock Sunday → user's current-week Sunday (column 6).
 * Either way the pattern (Mon content on Mon, Tue on Tue, ...) is preserved in
 * absolute weekday terms.
 */
const MOCK_WEEK_ANCHOR_DATE = '2026-03-22';

export const remapPersonalTimelineMockToCurrentWeek = (mock = RAW_PERSONAL_TIMELINE_WEEK) => {
	const userTimezone = moment.tz.guess();
	const userLocale = Intl.DateTimeFormat().resolvedOptions().locale || 'en-US';
	const isUSLocale = userLocale.startsWith('en-US');

	const anchorDay = moment.tz(MOCK_WEEK_ANCHOR_DATE, userTimezone).startOf('day');

	const today = moment.tz(userTimezone).startOf('day');
	const weekStart = isUSLocale
		? today.clone().startOf('week') // Sunday
		: today.clone().isoWeekday(1); // Monday of current ISO week
	const targetSunday = isUSLocale ? weekStart.clone() : weekStart.clone().add(6, 'days');

	const dayOffset = targetSunday.diff(anchorDay, 'days');

	return remapMock(mock, dayOffset);
};

export const MOCKED_PERSONAL_TIMELINE = remapPersonalTimelineMockToCurrentWeek();

export const injectPersonalTimelineMock = (response, mock = MOCKED_PERSONAL_TIMELINE) => {
	const results = response?.crewMemberTimeline?.results;
	if (!Array.isArray(results) || results.length === 0) return response;

	const base = results[0];
	const patchedDriver = {
		...base,
		activitiesV2: mock.activitiesV2 ?? base.activitiesV2,
		shifts: mock.shifts ?? base.shifts,
	};

	return {
		...response,
		crewMemberTimeline: {
			...response.crewMemberTimeline,
			results: [patchedDriver, ...results.slice(1)],
		},
	};
};
